import { useTheme } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { cn } from 'src/utils/utility';
import { LegTransportDetailDto } from 'src/generated/services/TFinancialApi';
import { CustomTooltip } from 'src/presentations/components/atom/customtooltip';
import IconInfo from 'src/assets/images/icons/info-circle-grey.svg';
import IconTruck from './icons/Truck';
import IconShip from './icons/Ship';
import IconAeroplane from './icons/Aeroplane';
import IconCt from './icons/Ct';
import IconCourier from './icons/Courier';

interface ITransportProps {
  type: LegTransportDetailDto['transportMedium'];
  disabled?: boolean;
  title?: string;
  tooltipContent?: ReactNode;
}

const Transport: React.FC<ITransportProps> = ({ type, disabled, title, tooltipContent }) => {
  const theme = useTheme();

  const { transportBorder, transportTruck, transportShip, borderGreen } = theme.palette.common;

  const iconColor = useMemo(() => {
    if (disabled) return transportBorder;
    if (type === 'ROAD') return transportTruck;
    if (type === 'CT') return borderGreen;
    if (type === 'AIR') return '#475B69';
    if (type === 'SEA') return '#30C5FF';
    if (type === 'COURIER') return '#475B69';
    return transportShip;
  }, [borderGreen, disabled, transportBorder, transportShip, transportTruck, type]);

  const bgColorClass = useMemo(() => {
    if (disabled) return `bg-grey-40`;
    if (type === 'ROAD') return `bg-orange-30`;
    if (type === 'AIR') return 'bg-[#475B69]';
    if (type === 'CT') return 'bg-[#00992B]';
    if (type === 'SEA') return 'bg-[#30C5FF]';
    if (type === 'COURIER') return 'bg-[#475B69]';
    return `bg-blue-30`;
  }, [disabled, type]);

  const borderColorClass = useMemo(() => {
    if (disabled) return `border-grey-40`;
    if (type === 'ROAD') return `border-orange-30`;
    if (type === 'AIR') return 'border-[#475B69]';
    if (type === 'CT') return 'border-[#00992B]';
    if (type === 'SEA') return 'border-[#30C5FF]';
    if (type === 'COURIER') return 'border-[#475B69]';
    return `border-blue-30`;
  }, [disabled, type]);

  return (
    <div className='relative flex-1 flex flex-col items-center'>
      {title && (
        <div className='flex items-center justify-center gap-6 mb-12 w-[124px]'>
          <h3 className={cn('text-12 xl:text-14 leading-16 xl:leading-18 h-16 xl:h-18 truncate text-center', title.includes(':') && 'opacity-0')}>{title}</h3>
          {tooltipContent && !title.includes(':') && (
            <CustomTooltip title={tooltipContent}>
              <img width={20} src={IconInfo} alt='' />
            </CustomTooltip>
          )}
        </div>
      )}
      <div className={cn('w-full pt-4 flex items-center justify-center self-start', title ? 'pt-4' : 'pt-[32px] xl:pt-[36px]')}>
        <div className={cn('flex-1 h-2', bgColorClass)} />
        <div className={cn('flex items-center justify-center rounded-full bg-white w-40 xl:w-42 h-40 xl:h-42 border-2', borderColorClass)}>
          {type === 'ROAD' && <IconTruck color={iconColor} size={24} />}
          {type === 'SEA' && <IconShip color={iconColor} size={24} />}
          {type === 'AIR' && <IconAeroplane color={iconColor} size={24} />}
          {type === 'CT' && <IconCt color={iconColor} size={24} />}
          {type === 'COURIER' && <IconCourier color={iconColor} size={24} />}
        </div>
        <div className={cn('flex-1 h-2', bgColorClass)} />
      </div>
    </div>
  );
};

export default Transport;
